import { useState, useEffect } from 'react';
import { Megaphone, Search, CheckCircle2, XCircle, Loader2, RefreshCw, Sparkles, Copy, ExternalLink, Clock, Target, Zap, Play, Archive, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const CHANNELS = ['google', 'meta', 'tiktok'];

const STATUS_BADGE = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  active: { label: 'Active', cls: 'bg-blue-100 text-blue-700' },
  needs_refresh: { label: 'Needs Refresh', cls: 'bg-orange-100 text-orange-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  retired: { label: 'Retired', cls: 'bg-muted text-muted-foreground' },
};

const PRIORITY_BADGE = {
  high: { label: 'High Priority', cls: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700' },
  experimental: { label: 'Experimental', cls: 'bg-purple-100 text-purple-700' },
};

function copyText(text) {
  navigator.clipboard.writeText(text).then(() => toast.success('Copied')).catch(() => {});
}

function CopyBlock({ label, items }) {
  if (!items || items.length === 0) return null;
  const text = items.join('\n');
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</h5>
        <button onClick={() => copyText(text)} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
          <Copy className="w-3 h-3" /> Copy all
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span key={i} className="text-xs bg-white border border-border rounded-md px-2 py-1 cursor-pointer hover:border-primary"
            onClick={() => copyText(item)} title="Click to copy">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function PerformancePanel({ campaign, onUpdatePerformance }) {
  const [editing, setEditing] = useState(false);
  const perf = campaign.performance || {};
  const [form, setForm] = useState({
    campaign_name: perf.campaign_name || '', spend: perf.spend || 0, clicks: perf.clicks || 0,
    leads: perf.leads || 0, sales: perf.sales || 0, ctr: perf.ctr || 0, cpc: perf.cpc || 0,
    cpa: perf.cpa || 0, roas: perf.roas || 0, notes: perf.notes || '',
  });

  const handleSave = async () => {
    try {
      await onUpdatePerformance(campaign.campaign_id, form);
      setEditing(false);
      toast.success('Performance updated');
    } catch { toast.error('Update failed'); }
  };

  if (!editing) {
    const hasData = perf.spend > 0 || perf.clicks > 0;
    return (
      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Performance</h5>
          <button onClick={() => setEditing(true)} className="text-[10px] text-primary hover:underline">
            {hasData ? 'Edit' : 'Log performance'}
          </button>
        </div>
        {hasData ? (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-xs">
            {perf.campaign_name && <div><span className="text-muted-foreground">Name:</span> {perf.campaign_name}</div>}
            <div><span className="text-muted-foreground">Spend:</span> €{perf.spend}</div>
            <div><span className="text-muted-foreground">Clicks:</span> {perf.clicks}</div>
            <div><span className="text-muted-foreground">Leads:</span> {perf.leads}</div>
            <div><span className="text-muted-foreground">CTR:</span> {perf.ctr}%</div>
            <div><span className="text-muted-foreground">CPC:</span> €{perf.cpc}</div>
            {perf.cpa > 0 && <div><span className="text-muted-foreground">CPA:</span> €{perf.cpa}</div>}
            {perf.roas > 0 && <div><span className="text-muted-foreground">ROAS:</span> {perf.roas}x</div>}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No performance data logged yet.</p>
        )}
      </div>
    );
  }

  return (
    <div className="border-t border-border pt-3 space-y-2">
      <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Log Performance</h5>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { key: 'campaign_name', label: 'Campaign Name', type: 'text', span: true },
          { key: 'spend', label: 'Spend (€)', type: 'number' },
          { key: 'clicks', label: 'Clicks', type: 'number' },
          { key: 'leads', label: 'Leads', type: 'number' },
          { key: 'sales', label: 'Sales', type: 'number' },
          { key: 'ctr', label: 'CTR (%)', type: 'number' },
          { key: 'cpc', label: 'CPC (€)', type: 'number' },
          { key: 'cpa', label: 'CPA (€)', type: 'number' },
          { key: 'roas', label: 'ROAS (x)', type: 'number' },
        ].map(f => (
          <div key={f.key} className={f.span ? 'col-span-2 md:col-span-5' : ''}>
            <label className="text-[10px] text-muted-foreground">{f.label}</label>
            <input type={f.type} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
              className="w-full text-xs border border-border rounded px-2 py-1 bg-white mt-0.5"
              step={f.type === 'number' ? '0.01' : undefined} />
          </div>
        ))}
        <div className="col-span-2 md:col-span-5">
          <label className="text-[10px] text-muted-foreground">Notes</label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-white mt-0.5" />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary/90">Save</button>
        <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs bg-muted text-foreground rounded-md">Cancel</button>
      </div>
    </div>
  );
}

export default function MarketingTab({ campaigns, onApprove, onReject, onGenerate, onRefresh, onUpdatePerformance, onGetAngles }) {
  const [channel, setChannel] = useState('google');
  const [angle, setAngle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [channelFilter, setChannelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [prebuiltAngles, setPrebuiltAngles] = useState([]);
  const [showAngles, setShowAngles] = useState(false);

  useEffect(() => {
    if (onGetAngles) {
      onGetAngles().then(setPrebuiltAngles).catch(() => {});
    }
  }, []);

  const filtered = campaigns.filter(c => {
    if (channelFilter && c.channel !== channelFilter) return false;
    if (statusFilter && c.status !== statusFilter) return false;
    return true;
  });

  const handleGenerate = async () => {
    if (!angle.trim()) { toast.error('Enter or select a target audience angle'); return; }
    setGenerating(true);
    try {
      await onGenerate(channel, angle.trim());
      toast.success(`Campaign generation started for ${channel}`);
    } catch { toast.error('Failed to start generation'); }
    finally { setGenerating(false); }
  };

  const handleRefresh = async (campaignId) => {
    try {
      await onRefresh(campaignId);
      toast.success('Campaign refresh started');
    } catch { toast.error('Failed to start refresh'); }
  };

  const draftCount = campaigns.filter(c => c.status === 'draft').length;
  const refreshCount = campaigns.filter(c => c.status === 'needs_refresh' || (c.refresh_due && new Date(c.refresh_due) < new Date())).length;

  return (
    <div className="space-y-4" data-testid="marketing-tab">
      {/* Generation panel */}
      <div className="bg-white rounded-xl border border-border p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold">Generate Campaign</h3>
          {draftCount > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{draftCount} drafts</span>}
          {refreshCount > 0 && <span className="text-[10px] bg-orange-100 text-orange-700 rounded-full px-2 py-0.5">{refreshCount} need refresh</span>}
        </div>

        {/* Pre-built angles */}
        <button onClick={() => setShowAngles(!showAngles)} className="text-xs text-primary hover:underline flex items-center gap-1 mb-3" data-testid="toggle-angles-btn">
          {showAngles ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showAngles ? 'Hide' : 'Show'} pre-built audience angles
        </button>
        {showAngles && (
          <div className="grid md:grid-cols-2 gap-2 mb-4">
            {prebuiltAngles.map(a => {
              const pb = PRIORITY_BADGE[a.priority] || PRIORITY_BADGE.medium;
              return (
                <button key={a.id} onClick={() => { setAngle(a.label); setChannel(a.channel_hint || 'google'); }}
                  className={`text-left p-2 rounded-lg border text-xs hover:border-primary transition-colors ${angle === a.label ? 'border-primary bg-primary/5' : 'border-border'}`}
                  data-testid={`angle-${a.id}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">{a.label}</span>
                    <span className={`text-[9px] px-1 py-0.5 rounded ${pb.cls}`}>{pb.label}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Best on: {a.channel_hint}</p>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Channel</label>
            <select value={channel} onChange={e => setChannel(e.target.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-white" data-testid="marketing-channel-select">
              {CHANNELS.map(c => <option key={c} value={c}>{c === 'google' ? 'Google Ads' : c === 'meta' ? 'Meta Ads' : 'TikTok Ads'}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground mb-1 block">Target Audience</label>
            <input type="text" value={angle} onChange={e => setAngle(e.target.value)}
              placeholder="e.g. homeowners with high energy bills"
              className="text-sm border border-border rounded-lg px-3 py-2 bg-white w-full" data-testid="marketing-angle-input" />
          </div>
          <button onClick={handleGenerate} disabled={generating}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            data-testid="marketing-generate-btn">
            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Megaphone className="w-3 h-3" />}
            Generate
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1 bg-white" data-testid="campaign-channel-filter">
          <option value="">All channels</option>
          {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1 bg-white" data-testid="campaign-status-filter">
          <option value="">All statuses</option>
          {Object.entries(STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="text-xs text-muted-foreground self-center">{filtered.length} campaign(s)</span>
      </div>

      {/* Campaign cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-8 text-center">
          <Megaphone className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No campaigns yet. Generate your first campaign draft above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => {
            const sb = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
            const pb = PRIORITY_BADGE[c.priority] || null;
            const expanded = expandedId === c.campaign_id;
            const isExpired = c.refresh_due && new Date(c.refresh_due) < new Date();
            const creative = c.creative_brief || {};
            const landing = c.landing_page || {};

            return (
              <div key={c.campaign_id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isExpired ? 'border-orange-300' : c.priority === 'high' ? 'border-primary/30' : 'border-border'}`}
                data-testid={`campaign-${c.campaign_id}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : c.campaign_id)}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold uppercase text-primary">{c.channel}</span>
                        {pb && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${pb.cls}`}>{pb.label}</span>}
                        <span className="text-xs text-muted-foreground">{c.audience_angle?.slice(0, 60)}</span>
                      </div>
                      <p className="text-sm font-medium mt-1">{c.selling_angle}</p>
                      {c.best_next_action && (
                        <p className="text-xs text-primary mt-1 flex items-center gap-1">
                          <Target className="w-3 h-3" /> {c.best_next_action.slice(0, 100)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isExpired && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Stale</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-border p-4 bg-muted/30 space-y-4">
                    {/* Strategic recommendation */}
                    {c.best_next_action && (
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Zap className="w-3.5 h-3.5 text-primary" />
                          <span className="text-xs font-semibold text-primary">Best Next Action</span>
                        </div>
                        <p className="text-xs text-foreground">{c.best_next_action}</p>
                        {c.test_channel_first && (
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Test first on: <span className="font-semibold uppercase">{c.test_channel_first}</span>
                            {c.test_channel_reasoning && ` — ${c.test_channel_reasoning}`}
                          </p>
                        )}
                        {c.priority_reasoning && (
                          <p className="text-[10px] text-muted-foreground mt-1">Priority: {c.priority_reasoning}</p>
                        )}
                      </div>
                    )}

                    {/* Landing page mapping */}
                    {landing.target_page && (
                      <div className="bg-white rounded-lg border border-border p-3">
                        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Landing Page</h5>
                        <div className="grid md:grid-cols-2 gap-1.5 text-xs">
                          <div><span className="text-muted-foreground">Target page:</span> <span className="font-medium">{landing.target_page}</span></div>
                          <div><span className="text-muted-foreground">User type:</span> {landing.target_user_type}</div>
                          <div><span className="text-muted-foreground">Measure focus:</span> <span className="capitalize">{landing.target_measure_focus}</span></div>
                          <div className="md:col-span-2"><span className="text-muted-foreground">Flow:</span> {landing.target_flow}</div>
                        </div>
                      </div>
                    )}

                    {/* Ad copy */}
                    <CopyBlock label="Headlines" items={c.headlines} />
                    <CopyBlock label="Primary Text" items={c.primary_texts} />
                    <CopyBlock label="CTAs" items={c.ctas} />
                    {c.keyword_themes?.length > 0 && <CopyBlock label="Keyword Themes" items={c.keyword_themes} />}

                    {/* Creative brief */}
                    {creative.visual_direction && (
                      <div>
                        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Creative Brief</h5>
                        <div className="bg-white rounded-lg border border-border p-3 text-xs space-y-1">
                          <p><span className="font-semibold">Visual:</span> {creative.visual_direction}</p>
                          {creative.key_visual_elements?.length > 0 && (
                            <p><span className="font-semibold">Elements:</span> {creative.key_visual_elements.join(', ')}</p>
                          )}
                          {creative.video_script_outline && (
                            <p><span className="font-semibold">Video script:</span> {creative.video_script_outline}</p>
                          )}
                          {creative.color_mood && <p><span className="font-semibold">Mood:</span> {creative.color_mood}</p>}
                        </div>
                      </div>
                    )}

                    {/* Refresh info */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>Created: {new Date(c.created_at).toLocaleDateString()}</span>
                      {c.refresh_due && (
                        <span className={isExpired ? 'text-orange-600 font-medium' : ''}>
                          Refresh due: {new Date(c.refresh_due).toLocaleDateString()}
                        </span>
                      )}
                      {c.refresh_notes && <span>· {c.refresh_notes}</span>}
                    </div>

                    {/* Performance */}
                    <PerformancePanel campaign={c} onUpdatePerformance={onUpdatePerformance} />

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                      {c.status === 'draft' && (
                        <>
                          <button onClick={() => onApprove(c.campaign_id)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1" data-testid={`approve-campaign-${c.campaign_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Approve
                          </button>
                          <button onClick={() => onReject(c.campaign_id)} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Reject
                          </button>
                        </>
                      )}
                      {c.status === 'approved' && (
                        <button onClick={() => onApprove(c.campaign_id)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1">
                          <Play className="w-3 h-3" /> Mark Active
                        </button>
                      )}
                      {['approved', 'active', 'needs_refresh'].includes(c.status) && (
                        <button onClick={() => handleRefresh(c.campaign_id)} className="px-3 py-1.5 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center gap-1" data-testid={`refresh-campaign-${c.campaign_id}`}>
                          <RefreshCw className="w-3 h-3" /> Refresh
                        </button>
                      )}
                      {c.status !== 'retired' && c.status !== 'rejected' && (
                        <button onClick={() => onReject(c.campaign_id)} className="px-3 py-1.5 text-xs bg-muted text-foreground rounded-md hover:bg-muted/80 flex items-center gap-1">
                          <Archive className="w-3 h-3" /> Retire
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
